import {computed, Injectable, signal} from '@angular/core';
import {MessageService} from 'primeng/api';
import {
    AppError,
    createAppError,
    ErrorSeverity,
    ErrorSource,
} from '../models/errors/app-error.model';

@Injectable({
    providedIn: 'root',
})
export class ErrorHandlerService {
    private readonly _errors = signal<AppError[]>([]);

    readonly errors = this._errors.asReadonly();
    readonly hasErrors = computed(() => this._errors().length > 0);
    readonly lastError = computed(() => {
        const list = this._errors();
        return list.length ? list[list.length - 1] : null;
    });
    readonly fatalError = computed(
        () => this._errors().find((err) => err.severity === 'fatal') ?? null
    );

    private readonly HTTP_ERROR_MESSAGES: Record<number, string> = {
        400: 'Richiesta non valida. Controlla i dati inseriti.',
        401: 'Sessione scaduta. Effettua di nuovo il login.',
        403: 'Non hai i permessi per questa operazione.',
        404: 'Risorsa non trovata.',
        422: 'I dati inseriti non sono validi.',
        500: 'Errore del server. Riprova più tardi.',
        503: 'Servizio temporaneamente non disponibile.',
    };

    constructor(private readonly messageService: MessageService) {
    }

    addError(
        message: string,
        options: {
            severity?: ErrorSeverity;
            source?: ErrorSource;
            statusCode?: number;
            context?: string;
            originalError?: unknown;
            showToast?: boolean;
        } = {}
    ): AppError {
        const error = createAppError({
            message,
            severity: options.severity ?? 'error',
            source: options.source ?? 'runtime',
            statusCode: options.statusCode,
            context: options.context,
            originalError: options.originalError,
        });

        this._errors.update((list) => [...list, error]);

        if (options.showToast !== false) {
            this.messageService.add({
                severity: this.mapToastSeverity(error.severity),
                summary: this.buildToastSummary(error.severity),
                detail: error.message,
            });
        }

        return error;
    }

    addHttpError(
        statusCode: number,
        context?: string,
        originalError?: unknown,
        options: { showToast?: boolean } = {}
    ): AppError {
        const message =
            this.HTTP_ERROR_MESSAGES[statusCode] ?? 'Errore di rete imprevisto';
        const severity: ErrorSeverity = statusCode >= 500 ? 'error' : 'warning';

        return this.addError(message, {
            severity,
            source: 'http',
            statusCode,
            context,
            originalError,
            showToast: options.showToast,
        });
    }

    clearError(errorId: string): void {
        this._errors.update((list) => list.filter((err) => err.id !== errorId));
    }

    clearAllErrors(): void {
        this._errors.set([]);
    }

    clearBySource(source: ErrorSource): void {
        this._errors.update((list) => list.filter((error) => error.source !== source));
    }

    private buildToastSummary(severity: ErrorSeverity): string {
        switch (severity) {
            case 'info':
                return 'Informazione';
            case 'warning':
                return 'Attenzione';
            case 'fatal':
            case 'error':
            default:
                return 'Errore';
        }
    }

    private mapToastSeverity(
        severity: ErrorSeverity
    ): 'info' | 'warn' | 'error' {
        switch (severity) {
            case 'info':
                return 'info';
            case 'warning':
                return 'warn';
            case 'fatal':
            case 'error':
            default:
                return 'error';
        }
    }
}
